import { useEffect, useState } from 'react'
import { removeDataGovernor } from '../../../../api/client'
import type { DataGovernor } from '../../../../api/client'
import { useCurrentUserContext } from '../../../../contexts/CurrentUserContext'
import { errorMessage } from '../DataGovernorsPage.logic'

export interface RemoveGovernorOutcome {
  readonly changed: boolean
  readonly message: string
}

export interface UseRemoveGovernorDialogOptions {
  readonly governor: DataGovernor | null
  readonly environment: string
  readonly onClose: () => void
  readonly onGovernorRemoved: (outcome: RemoveGovernorOutcome) => void
}

export function useRemoveGovernorDialog({
  governor,
  environment,
  onClose,
  onGovernorRemoved,
}: UseRemoveGovernorDialogOptions) {
  const [dialogError, setDialogError] = useState<string | null>(null)
  const [isMutationInProgress, setIsMutationInProgress] = useState(false)
  const { currentUser } = useCurrentUserContext()

  // Each open targets a different governor, so drop any error left over from a previous one.
  useEffect(() => {
    setDialogError(null)
  }, [governor])

  function closeDialog() {
    if (!isMutationInProgress) {
      onClose()
    }
  }

  async function submit() {
    if (governor === null) {
      return
    }

    setIsMutationInProgress(true)
    setDialogError(null)

    try {
      const response = await removeDataGovernor({
        email: governor.email,
        hub: governor.hub,
        business_object: governor.business_object,
        environment,
        ...(currentUser?.email && { requested_by: currentUser.email }),
      })
      onGovernorRemoved({ changed: response.changed, message: response.message })
    } catch (submitError) {
      setDialogError(errorMessage(submitError))
    } finally {
      setIsMutationInProgress(false)
    }
  }

  return { dialogError, isMutationInProgress, closeDialog, submit }
}
